import { env } from '@/lib/env'
import { isGatewayStatus, noteConnectionFailure, sendConnectionReports } from './connection-reports'
import { ApiError } from './errors'

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface RequestOptions {
  method?: Method
  signal?: AbortSignal
  body?: unknown
  query?: Record<string, string | number | undefined>
}

/** The control panel's own CSRF cookie, separate from the customer app's XSRF-TOKEN. */
export const CSRF_COOKIE = env.xsrfCookie

function readCookie(name: string): string | null {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`))
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null
}

let csrfRequest: Promise<void> | null = null

async function ensureCsrfCookie(force = false): Promise<void> {
  if (!force && readCookie(CSRF_COOKIE)) return
  csrfRequest ??= fetch(`${env.apiUrl}/auth/csrf`, { credentials: 'include' })
    .then(() => undefined)
    .finally(() => {
      csrfRequest = null
    })
  await csrfRequest
}

/** The path the API saw, for connection reports (no query string). */
const apiPath = (path: string) => new URL(`${env.apiUrl}${path}`).pathname

/** A gateway's error page is HTML, not the API's JSON: then there's no body to read. */
function parseJson(text: string, ok: boolean): unknown {
  if (!text) return undefined
  if (ok) return JSON.parse(text) as unknown
  try {
    return JSON.parse(text) as unknown
  } catch {
    return undefined
  }
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${env.apiUrl}${path}`)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value))
  }
  return url.toString()
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  const xsrf = readCookie(CSRF_COOKIE)
  if (xsrf) headers['X-XSRF-TOKEN'] = xsrf

  // A closing page cancels browser fetches. Make that cancellation explicit so it
  // cannot be mistaken for an API outage by the connection reporter.
  const controller = new AbortController()
  const cancel = () => controller.abort()
  const cancellable = (options.method ?? 'GET') === 'GET'
  if (options.signal?.aborted) cancel()
  options.signal?.addEventListener('abort', cancel, { once: true })
  if (cancellable) window.addEventListener('pagehide', cancel, { once: true })
  try {
    return await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      credentials: 'include',
      signal: controller.signal,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    })
  } catch (error) {
    if (controller.signal.aborted) throw new DOMException('Request cancelled', 'AbortError')
    throw error
  } finally {
    options.signal?.removeEventListener('abort', cancel)
    if (cancellable) window.removeEventListener('pagehide', cancel)
  }
}

/** The only way the control panel talks to the API; errors become typed ApiErrors. */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'

  let response: Response
  try {
    if (method !== 'GET') await ensureCsrfCookie()
    response = await send(path, options)
    if (response.status === 419 && method !== 'GET') {
      await ensureCsrfCookie(true)
      response = await send(path, options)
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    noteConnectionFailure({ kind: 'network', method, path: apiPath(path) })
    throw new ApiError(0, { message: 'Cannot reach the Arva SEO API. Check your connection and try again.' })
  }

  // Cloudflare's or the server's own error page, not the API answering with its JSON.
  if (isGatewayStatus(response.status) && !response.headers.get('Content-Type')?.includes('json')) {
    noteConnectionFailure({ kind: 'gateway', status: response.status, method, path: apiPath(path) })
  } else {
    sendConnectionReports()
  }

  if (response.status === 204) return undefined as T
  const text = await response.text()
  const json = parseJson(text, response.ok)
  if (!response.ok) {
    throw new ApiError(response.status, typeof json === 'object' && json !== null ? json : {})
  }

  return json as T
}

/** Download a file the API streams (e.g. the customer CSV) with the staff session. */
export async function downloadFile(path: string, query?: RequestOptions['query']): Promise<void> {
  let response: Response
  try {
    response = await send(path, { query })
  } catch {
    throw new ApiError(0, { message: 'Cannot reach the Arva SEO API. Check your connection and try again.' })
  }
  if (!response.ok) {
    const text = await response.text()
    let body: object = {}
    try {
      body = text ? (JSON.parse(text) as object) : {}
    } catch {
      /* not JSON */
    }
    throw new ApiError(response.status, body)
  }
  const name =
    /filename="?([^";]+)"?/.exec(response.headers.get('Content-Disposition') ?? '')?.[1] ??
    'arvaseo-export.csv'
  const url = URL.createObjectURL(await response.blob())
  const link = Object.assign(document.createElement('a'), { href: url, download: name })
  document.body.append(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export const http = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    api<T>(path, { query, signal }),
  post: <T = undefined>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body: body ?? {} }),
  put: <T = undefined>(path: string, body: unknown) => api<T>(path, { method: 'PUT', body }),
  patch: <T = undefined>(path: string, body: unknown) => api<T>(path, { method: 'PATCH', body }),
  delete: <T = undefined>(path: string) => api<T>(path, { method: 'DELETE' }),
}
